import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

/**
 * CODE PANEL (CONTEXT-DOCS.md §P4) — code shown Slack-style: a SHADED box with a header (title · language · lines)
 * and syntax colouring (the workspace viewer's highlighter, code-split — loaded only once a panel renders). Long
 * code COLLAPSES to the first ``previewLines``; "Show N more lines" expands it. When the caller holds only the TOP of
 * a file (``truncated``), expanding calls ``onExpand()`` to load the rest first — a huge file is never downloaded
 * unless someone asks to read it.
 *
 * Used by: code files in the room stream (CodeFilePanel), ```fences in chat messages (MarkdownRenderer size="chat").
 */
export const PREVIEW_LINES = 15;
// FOLDED CODE FADES OUT (the long-message look, MODES.md §3d): a mask on the code itself — works on any shade
const _fade = 'linear-gradient(to bottom, #000 calc(100% - 3rem), transparent)';
export const CODE_FADE = { maskImage: _fade, WebkitMaskImage: _fade };

let _hl = null;                                   // the highlighter module, once loaded (shared by every panel)
function loadHighlighter() {
  if (!_hl) _hl = import('../utils/codeHighlight.js').catch(() => { _hl = null; return null; });
  return _hl;
}

export function splitLines(code = '') {
  const s = String(code || '').replace(/\r\n/g, '\n').replace(/\n$/, '');
  return s ? s.split('\n') : [];
}

export default function CodePanel({
  code = '', grammar = '', title = '', meta = '', actions = null, previewLines = PREVIEW_LINES,
  truncated = false, onExpand = null, moreLabel = '', onTitleClick = null, testId = 'code-panel', size = 'chat', getCopyText = null, placeholder = '',
}) {
  const lines = useMemo(() => splitLines(code), [code]);
  const long = truncated || lines.length > previewLines;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const shown = open || !long ? lines : lines.slice(0, previewLines);
  const text = shown.join('\n');

  const [html, setHtml] = useState(null);
  useEffect(() => {
    let gone = false;
    setHtml(null);
    if (!text) return undefined;
    loadHighlighter().then((m) => { if (!gone && m) setHtml(m.highlightCode(text, grammar)); });
    return () => { gone = true; };
  }, [text, grammar]);

  const expand = async () => {
    setError('');
    if (truncated && typeof onExpand === 'function') {
      setLoading(true);
      try { await onExpand(); } catch (e) { setError((e && e.message) || "Couldn't load the rest."); setLoading(false); return; }
      setLoading(false);
    }
    setOpen(true);
  };

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      // a file holding only its top copies the WHOLE file (the caller loads it)
      const all = typeof getCopyText === 'function' ? await getCopyText() : code;
      await navigator.clipboard.writeText(String(all || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — nothing to do */ }
  };

  const more = lines.length - previewLines;
  const label = truncated ? (moreLabel || 'Show the rest') : `Show ${more} more line${more === 1 ? '' : 's'}`;
  const T = size === 'compact' ? 'text-[10px]' : 'text-[0.8125rem]';
  return (
    <div data-testid={testId} data-lines={lines.length} data-open={open ? 'true' : 'false'}
      className="my-1 w-full max-w-3xl overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-2.5 py-1">
        <span className="font-mono text-[0.75rem] font-semibold text-slate-500" aria-hidden>&lt;/&gt;</span>
        {title ? (
          onTitleClick ? (
            <button type="button" onClick={onTitleClick} data-testid={`${testId}-title`}
              className="min-w-0 truncate text-[0.8125rem] font-semibold text-slate-800 hover:text-indigo-700 hover:underline">{title}</button>
          ) : <span data-testid={`${testId}-title`} className="min-w-0 truncate text-[0.8125rem] font-semibold text-slate-800">{title}</span>
        ) : null}
        {meta ? <span data-testid={`${testId}-meta`} className="truncate text-[0.6875rem] text-slate-500">{meta}</span> : null}
        <span className="ml-auto inline-flex items-center gap-0.5">
          {actions}
          <button type="button" onClick={copy} data-testid={`${testId}-copy`} aria-label="Copy" title={copied ? 'Copied' : 'Copy'}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900">
            {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
          </button>
        </span>
      </div>
      <pre className={`hljs m-0 overflow-x-auto bg-transparent px-3 py-2 font-mono ${T} leading-[1.5]`} data-testid={`${testId}-code`}
        data-faded={long && !open ? 'true' : undefined} style={long && !open ? CODE_FADE : undefined}>
        {html != null
          ? <code dangerouslySetInnerHTML={{ __html: html }} />
          : <code className="whitespace-pre">{text || <span className="text-slate-400">{placeholder}</span>}</code>}
      </pre>
      {long ? (
        <div className="border-t border-slate-200 bg-slate-100/70 px-2.5 py-1">
          {open ? (
            <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-collapse`}
              className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-slate-600 hover:text-slate-900">
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />Collapse
            </button>
          ) : (
            <button type="button" onClick={expand} disabled={loading} data-testid={`${testId}-expand`}
              className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-60">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
              {loading ? 'Loading…' : label}
            </button>
          )}
          {error ? <span className="ml-2 text-[0.75rem] text-rose-600">{error}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
